import DropdownMenu from '../DropdownMenu';
import UserIcon from '../UserIcon';

import { CSTriggerSourceFromHeaderTitlesMap, HeaderTitles } from '../../constants';

import HeaderNavigationClickstreamEvents from '../../utils/clickstream';

import NotificationCenter from '../NotificationCenter';

import { Box, Divider, Icon, List, Menu, Paragraph, Text, TextButton } from '@zillow/constellation-10';
import { IconExternalFilled, IconUserSettingsFilled } from '@zillow/constellation-icons';

import { HeaderButton } from '../HeaderButton';

// style
import { menuItemWithIconStyles } from './styles';
import { headerNavButtonStyles } from '../HeaderButton/styles';

import type { Displays, User } from '../../types';

export type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  paths: any;
  pathname: string;
  userModel: User | null;
  displays: Displays;
  helpPage: string;
};

const SectionRight = ({ displays, paths, pathname, userModel, helpPage }: Props) => {
  const {
    billingPage,
    billingDue,
    contactSupportPage,
    resourcesPage,
    zrmSignInPage: signInPageFunc,
    mfhResourcesPage,
    postLogout,
    securitySettingsPage,
    adManagementPage,
    setupTasks,
  } = paths;

  const { email, firstName, lastName, loggedIn, userName, profileImageUrl } = userModel ?? {};

  // functions that return paths
  const signInPage = signInPageFunc(pathname);

  const {
    displayAccessMainContactSupport,
    displayBilling,
    displayMainBilling,
    displayMFHResources,
    displayMainAccountSettings,
    displayMainHelp,
    displayMainNotificationCenter,
    displayMainResources,
    displayMainSecuritySettings,
    displayAdManagement,
  } = displays;

  const handleLinkClick = (pageClickingTo: string) => {
    if (loggedIn && userModel) {
      const csLink = CSTriggerSourceFromHeaderTitlesMap[pageClickingTo];

      if (csLink) {
        HeaderNavigationClickstreamEvents.ClickNavigationLink({ user: userModel, link: csLink });
      } else {
        // eslint-disable-next-line
        console.error('ERROR@handleLinkClick: trigger source does not exist.', csLink);
      }
    }
  };

  return (
    <List.Root
      appearance="none"
      css={{
        display: 'flex',
        alignItems: 'center',
        flexBasis: '100%',
        flexFlow: 'row nowrap',
        justifyContent: 'flex-end',
        listStyleType: 'none',
      }}
    >
      <HeaderButton
        title={HeaderTitles.backToBrand}
        onClick={() => handleLinkClick(HeaderTitles.backToBrand)}
        path={paths.homePage}
      >
        {HeaderTitles.backToBrand}
      </HeaderButton>

      {Boolean(displayMFHResources || displayMainResources || displayMainHelp || displayAccessMainContactSupport) && (
        <li>
          <Box css={{ paddingRight: '800' }}>
            <DropdownMenu
              title={
                <TextButton tone="neutral" textStyle="body" css={headerNavButtonStyles}>
                  Help
                </TextButton>
              }
            >
              <>
                {Boolean(displayMFHResources || displayMainResources) && (
                  <div>
                    {displayMFHResources ? (
                      <Menu.Item
                        onClick={() => handleLinkClick(HeaderTitles.resources)}
                        css={menuItemWithIconStyles}
                        asChild
                      >
                        <a className="Link" target="_blank" href={mfhResourcesPage} rel="noreferrer">
                          <Text textStyle="body-sm-bold">{HeaderTitles.mfhResources}</Text>
                          <Icon
                            render={<IconExternalFilled />}
                            data-testid="IconExternal"
                            css={{ color: 'icon.accent.brand.hero' }}
                          />
                        </a>
                      </Menu.Item>
                    ) : (
                      <Menu.Item
                        onClick={() => handleLinkClick(HeaderTitles.resources)}
                        css={menuItemWithIconStyles}
                        asChild
                      >
                        <a className="Link" target="_blank" href={resourcesPage} rel="noreferrer">
                          <Text textStyle="body-sm-bold">{HeaderTitles.resources}</Text>
                          <Icon
                            render={<IconExternalFilled />}
                            data-testid="IconExternal"
                            css={{ color: 'icon.accent.brand.hero' }}
                          />
                        </a>
                      </Menu.Item>
                    )}
                  </div>
                )}

                {displayMainHelp && (
                  <Menu.Item onClick={() => handleLinkClick(HeaderTitles.help)} css={menuItemWithIconStyles} asChild>
                    <a className="Link" target="_blank" href={helpPage ?? ''} rel="noreferrer">
                      <Text textStyle="body-sm-bold">{HeaderTitles.help}</Text>
                      <Icon
                        render={<IconExternalFilled />}
                        data-testid="IconExternal"
                        css={{ color: 'icon.accent.brand.hero' }}
                      />
                    </a>
                  </Menu.Item>
                )}

                {displayAccessMainContactSupport && (
                  <Menu.Item
                    onClick={() => handleLinkClick(HeaderTitles.contactSupport)}
                    css={menuItemWithIconStyles}
                    asChild
                  >
                    <a className="Link" target="_blank" href={contactSupportPage} rel="noreferrer">
                      <Text textStyle="body-sm-bold">{HeaderTitles.contactSupport}</Text>
                      <Icon
                        render={<IconExternalFilled />}
                        data-testid="IconExternal"
                        css={{ color: 'icon.accent.brand.hero' }}
                      />
                    </a>
                  </Menu.Item>
                )}
              </>
            </DropdownMenu>
          </Box>
        </li>
      )}

      {loggedIn && displayMainNotificationCenter && (
        <li className="SectionRight-notificationCenter">
          <Box css={{ paddingRight: '800' }}>
            <NotificationCenter />
          </Box>
        </li>
      )}

      {loggedIn && (
        <DropdownMenu
          title={
            <TextButton aria-label="My account" tone="neutral" textStyle="body">
              <UserIcon
                profileImageUrl={profileImageUrl}
                firstName={firstName}
                lastName={lastName}
                userName={userName}
                email={email}
              />
            </TextButton>
          }
        >
          <>
            {displayMainAccountSettings && (
              <Menu.Item
                onClick={() => handleLinkClick(HeaderTitles.accountSettings)}
                css={menuItemWithIconStyles}
                asChild
              >
                <a href={paths.accountSettingsPage}>
                  <div>
                    <Paragraph textStyle="body-sm-bold">{HeaderTitles.accountSettings}</Paragraph>
                  </div>

                  <Icon render={<IconUserSettingsFilled />} css={{ color: 'icon.accent.brand.hero' }} />
                </a>
              </Menu.Item>
            )}

            {displayMainSecuritySettings && (
              <Menu.Item
                onClick={() => handleLinkClick(HeaderTitles.securitySettings)}
                css={menuItemWithIconStyles}
                asChild
              >
                <a href={securitySettingsPage}>
                  <Text textStyle="body-sm-bold">{HeaderTitles.securitySettings}</Text>

                  <Icon
                    render={<IconExternalFilled />}
                    data-testid="IconExternal"
                    css={{ color: 'icon.accent.brand.hero' }}
                  />
                </a>
              </Menu.Item>
            )}

            {displayBilling && !displayMainBilling && (
              <Menu.Item asChild onClick={() => handleLinkClick(HeaderTitles.billing)}>
                <a href={`${billingPage}/${billingDue}`}>
                  <Text textStyle="body-sm-bold">{HeaderTitles.billing}</Text>
                </a>
              </Menu.Item>
            )}

            {displayAdManagement && (
              <Menu.Item asChild onClick={() => handleLinkClick(HeaderTitles.adManagement)}>
                <a href={`${adManagementPage}/${setupTasks}`}>
                  <Text textStyle="body-sm-bold">{HeaderTitles.adManagement}</Text>
                </a>
              </Menu.Item>
            )}

            <Divider />

            <Menu.Item asChild onClick={() => handleLinkClick(HeaderTitles.signOut)}>
              <a href={postLogout}>
                <Text textStyle="body-sm-bold">{HeaderTitles.signOut}</Text>
              </a>
            </Menu.Item>
          </>
        </DropdownMenu>
      )}

      {!loggedIn && (
        <HeaderButton title="Sign in" path={signInPage}>
          Sign in
        </HeaderButton>
      )}
    </List.Root>
  );
};

export default SectionRight;
